import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import { promptCrazyGamesLogin } from '../services/crazygamesAuth';
import { useMuted, toggleMuted, sfx } from '../services/sfx';
import { useOnboardingNavHint } from '../hooks/useOnboarding';
import { useMinigameAccessStore } from '../stores/minigameAccessStore';
import { PlayerRole } from '../types';

export default function Navbar() {
  const { isAuthenticated, role, logout, authSource, userId } = useAuthStore();
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const muted = useMuted();
  const navHint = useOnboardingNavHint();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  // Ring the nav entry the tutorial is currently pointing at (no-op once it's done).
  const hintClass = (path: string) => (navHint === path && !isActive(path) ? 'nav-hint' : undefined);

  // Minigames: staff always, everyone else once an admin has published one (server-decided).
  const minigamesVisible = useMinigameAccessStore(s => s.visible);

  // A CrazyGames guest can upgrade to their CrazyGames account (progress migrates).
  const isCrazyGuest = authSource === 'crazygames' && !!userId?.startsWith('cgguest-');

  // Not signed in → the only reachable page is the login/home screen (all game
  // routes redirect there), so a nav bar with redundant "Home"/"Login" links
  // pointing at the same page adds nothing. Hide it; the hero shows the branding.
  if (!isAuthenticated) return null;

  return (
    <nav className="navbar">
      <Link
        to="/"
        className="navbar-brand"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem' }}
      >
        <img src="/logo.svg" alt="ROOTKIN" width={28} height={28} style={{ display: 'block' }} />
        <span className="navbar-brand-text" style={{ letterSpacing: '0.18em' }}>ROOTKIN</span>
      </Link>

      <ul className="navbar-nav">
        <li>
          <Link to="/" className={hintClass('/')} style={{ color: isActive('/') ? 'var(--primary-color)' : undefined }}>
            Home
          </Link>
        </li>
        <li>
          <Link to="/missions" className={hintClass('/missions')} style={{ color: isActive('/missions') ? 'var(--primary-color)' : undefined }}>
            Missions
          </Link>
        </li>
        {/* Hidden until this player may actually use it — the server enforces the same rule
            on every minigame endpoint; this only keeps the menu honest. */}
        {minigamesVisible && (
          <li>
            <Link to="/minigames" className={hintClass('/minigames')} style={{ color: isActive('/minigames') ? 'var(--primary-color)' : undefined }}>
              Minigames
            </Link>
          </li>
        )}
        <li>
          <Link to="/store" className={hintClass('/store')} style={{ color: isActive('/store') ? 'var(--primary-color)' : undefined }}>
            Store
          </Link>
        </li>
        <li>
          <Link to="/leaderboard" style={{ color: isActive('/leaderboard') ? 'var(--primary-color)' : undefined }}>
            Leaderboard
          </Link>
        </li>
        <li>
          <Link
            to="/notifications"
            style={{
              color: isActive('/notifications') ? 'var(--primary-color)' : undefined,
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
            }}
          >
            Notifications
            {unreadCount > 0 && (
              <span style={{
                background: '#ef4444',
                color: '#fff',
                borderRadius: '999px',
                fontSize: '0.62rem',
                fontWeight: 'bold',
                padding: '0 0.35rem',
                lineHeight: '1.5',
                minWidth: '1.1rem',
                textAlign: 'center',
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>
        </li>
        <li>
          <Link to="/hacked-nodes" style={{ color: isActive('/hacked-nodes') ? 'var(--primary-color)' : undefined }}>
            Hacked Nodes
          </Link>
        </li>
        <li>
          <Link to="/cooldowns" style={{ color: isActive('/cooldowns') ? 'var(--primary-color)' : undefined }}>
            Targets
          </Link>
        </li>
        <li>
          <Link to="/profile" style={{ color: isActive('/profile') ? 'var(--primary-color)' : undefined }}>
            Profile
          </Link>
        </li>
        {(role === PlayerRole.Admin || role === PlayerRole.Moderator) && (
          <li>
            <Link
              to="/admin"
              style={{ color: isActive('/admin') ? 'var(--primary-color)' : undefined, fontWeight: 'bold' }}
            >
              {role === PlayerRole.Admin ? '[ADMIN]' : '[MOD]'}
            </Link>
          </li>
        )}
        {/* Sound on/off (synthesised SFX; preference is remembered). */}
        <li>
          <button
            onClick={() => { toggleMuted(); if (muted) sfx.click(); }}
            title={muted ? 'Sound off — click to enable' : 'Sound on — click to mute'}
            aria-label={muted ? 'Enable sound' : 'Mute sound'}
            style={{
              padding: '0.2em 0.5em', fontSize: '1em', lineHeight: 1,
              background: 'transparent', border: '1px solid var(--border-color)',
              borderRadius: '4px', cursor: 'pointer',
              color: muted ? 'var(--text-muted)' : 'var(--primary-color)',
            }}
          >
            {muted ? '🔇' : '🔊'}
          </button>
        </li>
        {/* CrazyGames guests can log in to their CrazyGames account (progress migrates). */}
        {isCrazyGuest && (
          <li>
            <button
              onClick={() => { void promptCrazyGamesLogin(); }}
              style={{ padding: '0.3em 0.8em', fontSize: '0.9em', fontWeight: 'bold', whiteSpace: 'nowrap' }}
            >
              Log in with CrazyGames
            </button>
          </li>
        )}
        {/* Logout is shown for the username ('dev') login. */}
        {authSource === 'dev' && (
          <li>
            <button onClick={() => logout()} style={{ padding: '0.3em 0.8em', fontSize: '0.9em' }}>
              Logout
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}
